import React from 'react';
import { createPaginator } from '@orange-marmalade/paginate-this';
import { fromJS } from 'immutable';
import { createDuck } from 'redux-duck';
import PaginateThisDuckCreator from '../middleware/paginate-this';
import { api, cleanQuery, formatError } from '../middleware/client';
import { Agent as AgentRecord } from '../records';

const BASE_URL = `${process.env.REACT_APP_REST_URI}/agents`;
const locator = ({ AgentSearch }) => AgentSearch.get('list');
const listId = 'agents';
const defaultQuery = {
  page: 1,
  pageSize: 16,
  sort: '',
  sortReverse: true
};
const sortFields = [
  {
    id: '',
    name: 'Default',
    order: 'DESC'
  },
  {
    id: 'lastName',
    name: 'Last Name: A-Z',
    order: 'ASC'
  },
  {
    id: 'lastName',
    name: 'Last Name: Z-A',
    order: 'DESC'
  }
];

const paginateReducer = createPaginator({
  listId,
  fetch,
  locator,
  cache: true,
  initialSettings: {
    // initialized is true to prevent extra actions being fired
    // since we actually initialize the list using query parameters
    initialized: true,
    ...defaultQuery
  }
});

const duck = createDuck('AgentSearch', 'nabor');
const LOAD_TYPE = duck.defineType('LOAD');
const LOAD_SUCCESS_TYPE = duck.defineType('LOAD_SUCCESS');
const LOAD_FAIL_TYPE = duck.defineType('LOAD_FAIL');
const ABORTED_REQUEST_TYPE = duck.defineType('ABORTED_REQUEST');
const ABORTED_REQUEST = duck.createAction(ABORTED_REQUEST_TYPE);
const LOAD_CATS_TYPE = duck.defineType('LOAD_CATS');
const LOAD_CATS_SUCCESS_TYPE = duck.defineType('LOAD_CATS_SUCCESS');
const LOAD_CATS_FAIL_TYPE = duck.defineType('LOAD_CATS_FAIL');

// custom abort support
var lastRequest;

const initialState = fromJS({
  list: paginateReducer(),
  detail: {
    loaded: false,
    loading: false,
    record: new AgentRecord()
  },
  category: {
    loaded: false,
    loading: false,
    results: []
  }
});

function reducer(state = initialState, action) {
  if (action.type === LOAD_TYPE) {
    state = state.mergeIn(['detail'], {
      loaded: action.id,
      loading: true,
      error: null
    });
  } else if (action.type === LOAD_SUCCESS_TYPE) {
    state = state.mergeIn(['detail'], {
      loaded: action.id,
      loading: false,
      record: new AgentRecord(action.payload)
    });
  } else if (action.type === LOAD_FAIL_TYPE) {
    state = state.mergeIn(['detail'], {
      loading: false,
      error: formatError(action.error)
    });
  } else if (action.type === LOAD_CATS_TYPE) {
    state = state.mergeIn(['category'], {
      loaded: false,
      loading: true,
      error: null
    });
  } else if (action.type === LOAD_CATS_SUCCESS_TYPE) {
    state = state.mergeIn(['category'], {
      loaded: true,
      loading: false,
      results: fromJS(action.payload.list)
    });
  } else if (action.type === LOAD_CATS_FAIL_TYPE) {
    state = state.mergeIn(['category'], {
      loading: false,
      error: formatError(action.error)
    });
  } else if (action.type === ABORTED_REQUEST_TYPE) {
    state = state.setIn(['list', 'isLoading'], false);
  } else {
    state = state.setIn(['list'], paginateReducer.call(this, state.get('list'), action));
  }
  return state;
}

function abortRequest() {
  if (lastRequest && lastRequest.abort) {
    lastRequest.abort();
  }

  return ABORTED_REQUEST(lastRequest);
}

function fetch(params) {
  const { id, query } = params;

  if (id) {
    let url = `${BASE_URL}/${id}`;
    return {
      types: [LOAD_TYPE, LOAD_SUCCESS_TYPE, LOAD_FAIL_TYPE],
      promise: client =>
        client.get(url).then(function (response) {
          return response;
        }),
      id
    };
  } else {
    lastRequest = api.get(BASE_URL, {
      query: cleanQuery({
        ...query,
        page: undefined,
        pageSize: undefined,
        offset: query.pageSize * (query.page - 1),
        limit: query.pageSize
      })
    });

    return lastRequest
      .then(function (response) {
        return {
          data: {
            results: response.list.map(function (agent) {
              agent = new AgentRecord(agent).toJS();

              return agent;
            }),
            total_count: response.total
          }
        };
      })
      .catch(error => {
        throw formatError(error);
      });
  }
}

function fetchCategory() {
  let url = `${process.env.REACT_APP_REST_URI}/category`;
  return {
    types: [LOAD_CATS_TYPE, LOAD_CATS_SUCCESS_TYPE, LOAD_CATS_FAIL_TYPE],
    promise: client =>
      client.get(url).then(function (response) {
        return response;
      })
  };
}

// const parseNumber = v => {
//   v = parseInt(v, 10);

//   return isNaN(v) ? '' : v;
// };

const paginateThis = new PaginateThisDuckCreator({
  listId,
  locator,
  defaultQuery,
  abortRequest,
  // Lists filters that will automatically get parsed from the query string
  availableFilters: {
    memberType: undefined,
    officeId: undefined,
    address: undefined,
    firm: undefined,
    designation: undefined,
    language: undefined,
    name: undefined,
    categoryId: undefined
  }
});

const MEMBER_TYPE_MAP = {
  realtor: {
    url: '/find-naples-agent',
    label: (
      <span>
        REALTOR
        <sup>®</sup>
      </span>
    )
  },
  brokerage: {
    url: '/find-naples-firm',
    label: 'Brokerage'
  },
  service_provider: {
    url: '/find-naples-agent?memberType=service_provider',
    label: 'Service Provider'
  },
  service_company: {
    url: '/find-naples-firm?officeType=service_company',
    label: 'Service Company'
  }
};

export { paginateThis, fetch, fetchCategory, sortFields, AgentRecord, MEMBER_TYPE_MAP };
export default reducer;



// WEBPACK FOOTER //
// ./src/ducks/AgentSearch.js